import Loader from "../../components/Loader";
import {useQuery} from "@tanstack/react-query";
import CreditNote from "./CreditNote";
import {useContext, useEffect, useState} from "react";
import Paginator from "../../components/Paginator";
import {Col, Form, FormFeedback, Row, Input, Label, Button} from "reactstrap";
import AuthContext from "../../context/AuthContext";
import handleUnauthorized401QueryError from "../../utils/handleUnauthorized401QueryError";
import {getCreditNotes, exportCreditNoteGST} from "../../api/api";
import {toast} from "react-toastify";
import RecordsNotFound from "../../components/RecordsNotFound";
import {useLocation, useNavigate} from "react-router-dom";


function CreditNotePage() {
    const {refreshToken} = useContext(AuthContext)
    const location = useLocation();
    const cnInvoiceFromState = location.state?.cnInvoice || "";
    const [page, setPage] = useState(1);
    const [cnId, setCnId] = useState("");
    const [cnInvoice, setCnInvoice] = useState(cnInvoiceFromState);
    const [cnStatus, setCnStatus] = useState("");
    const [cnType, setCnType] = useState("");
    const [cnCreatedDate, setCnCreatedDate] = useState("");
    const [cnUpdatedDate, setCnUpdatedDate] = useState("");

    const [queryKey, setQueryKey] = useState(['creditNotes', page])
    const [gstExportMonth, setGstExportMonth] = useState("");
    const [isExporting, setIsExporting] = useState(false);
    const navigate = useNavigate()

    useEffect(() => {
        setQueryKey(['creditNotes', page])
    }, [page]);

    useEffect(() => {
        if (!cnInvoiceFromState) return;
        setPage(1);
        setCnId('');
        setCnInvoice(cnInvoiceFromState);
        setCnStatus('');
        setCnType('');
        setCnCreatedDate('');
        setCnUpdatedDate('');
        setQueryKey(['creditNotes', 1, '', cnInvoiceFromState, '', '', '', '']);
    }, [cnInvoiceFromState]);


    const { status, data, error, isFetching, refetch } = useQuery({
        queryKey: queryKey,
        queryFn: () => getCreditNotes(page, cnId, cnInvoice, cnStatus, cnType, cnCreatedDate, cnUpdatedDate),
        keepPreviousData : true,
        retry: false,
        onError: (err => {
            handleUnauthorized401QueryError(err, refreshToken, refetch)
            if(err.response.status !== 401){
                toast.error(`An error occurred! ${err?.response?.data?.detail}`)
            }
            if(err.response.status === 403){
                navigate(-1)
            }
        })
        })

    const searchCreditNote = (e) =>{
        e.preventDefault()
        setPage(1)
        setQueryKey( ['creditNotes', page, cnId, cnInvoice, cnStatus, cnType, cnCreatedDate, cnUpdatedDate])
    }

    const handleGSTExport = async () => {
        if (!gstExportMonth) {
            toast.error("Please select a month to export.");
            return;
        }
        setIsExporting(true);
        try {
            const [year, month] = gstExportMonth.split('-').map(Number);
            const firstDay = `${year}-${String(month).padStart(2, '0')}-01`;
            const lastDay = new Date(year, month, 0).getDate();
            const lastDayStr = `${year}-${String(month).padStart(2, '0')}-${lastDay}`;
            const response = await exportCreditNoteGST(firstDay, lastDayStr);
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `GSTR1_CreditNotes_${gstExportMonth}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Export failed. No credit notes found for the selected period.");
        } finally {
            setIsExporting(false);
        }
    };

    const clearSearchCreditNote = (e) =>{
        e.preventDefault()
        setCnId('')
        setCnInvoice('')
        setCnStatus('')
        setCnType('')
        setCnCreatedDate('')
        setCnUpdatedDate('')
        setQueryKey( ['creditNotes', 1])
    }

    return (
        <>
            <div className="card-box">
                <div className="row">
                    <div className="col-sm-12">

                        <Form onSubmit={searchCreditNote}>
                            <Row className="form-row align-items-center">
                                <Col>
                                    <Label
                                        className="visually-hidden"
                                        for="cn-id"
                                    >
                                        ID
                                    </Label>
                                    <Input
                                        id="cn-id"
                                        name="cn-id"
                                        type="text"
                                        className="form-control mb-2"
                                        placeholder="ID"
                                        onChange={(e) =>
                                            setCnId(
                                                e.target.value
                                            )
                                        }
                                        value={cnId}
                                    />
                                </Col>
                                <Col>
                                    <Label
                                        className="visually-hidden"
                                        for="cn-invoice"
                                    >
                                        Invoice ID
                                    </Label>
                                    <Input
                                        id="cn-invoice"
                                        name="cn-invoice"
                                        type="text"
                                        className="form-control mb-2"
                                        placeholder="Invoice ID"
                                        onChange={(e) =>
                                            setCnInvoice(
                                                e.target.value
                                            )
                                        }
                                        value={cnInvoice}
                                    />
                                </Col>
                                <Col>
                                    <Label
                                        className="visually-hidden"
                                        for="cn-status"
                                    >
                                        Status
                                    </Label>
                                    <Input
                                        type="select"
                                        id="cn-status"
                                        className="form-control mb-2"
                                        value={cnStatus}
                                        onChange={(e) =>
                                            setCnStatus(
                                                e.target.value
                                            )
                                        }
                                    >
                                        <option value="">--</option>
                                        <option value={"ISSUED"}>
                                            Issued
                                        </option>
                                        <option value={"CANCELLED"}>
                                            Cancelled
                                        </option>
                                    </Input>
                                </Col>
                                <Col>
                                    <Label
                                        className="visually-hidden"
                                        for="cn-type"
                                    >
                                        Type
                                    </Label>
                                    <Input
                                        type="select"
                                        id="cn-type"
                                        className="form-control mb-2"
                                        value={cnType}
                                        onChange={(e) =>
                                            setCnType(
                                                e.target.value
                                            )
                                        }
                                    >
                                        <option value="">Type --</option>
                                        <option value={"FULL"}>
                                            Full
                                        </option>
                                        <option value={"PARTIAL"}>
                                            Partial
                                        </option>
                                    </Input>
                                </Col>
                                <Col>
                                    <Label
                                        className="visually-hidden"
                                        for="cn-date-created"
                                    >
                                        Date Created
                                    </Label>
                                    <Input
                                        id="cn-date-created"
                                        name="cn-date-created"
                                        type="date"
                                        className="form-control mb-2"
                                        onChange={(e) =>
                                            setCnCreatedDate(
                                                e.target.value
                                            )
                                        }
                                        value={cnCreatedDate}
                                    />
                                </Col>

                            </Row>
                            <Row className="form-row align-items-center">
                                <Col>
                                    <Button type="submit"
                                            className="btn bx-btn-primary waves-effect waves-light mr-2"
                                            disabled={!cnId && !cnInvoice && !cnStatus && !cnType && !cnCreatedDate}
                                    >
                                        Search
                                    </Button>

                                    <Button type="button"
                                            onClick={clearSearchCreditNote}
                                            className="btn bx-btn-primary waves-effect waves-light"
                                            disabled={!cnId && !cnInvoice && !cnStatus && !cnType && !cnCreatedDate}
                                    >
                                        Clear
                                    </Button>
                                </Col>
                            </Row>
                        </Form>
                        <div className="d-flex align-items-end mt-3" style={{gap: '10px'}}>
                            <div>
                                <Label for="gst-export-month-cn" className="form-label">GST Export Month</Label>
                                <Input
                                    id="gst-export-month-cn"
                                    type="month"
                                    className="form-control"
                                    value={gstExportMonth}
                                    onChange={(e) => setGstExportMonth(e.target.value)}
                                />
                            </div>
                            <Button
                                type="button"
                                className="btn bx-btn-primary waves-effect waves-light"
                                onClick={handleGSTExport}
                                disabled={!gstExportMonth || isExporting}
                            >
                                {isExporting ? "Exporting..." : "Export GST"}
                            </Button>
                        </div>
                    </div>
                </div>
            </div>

            {status === 'loading' && (
                <Loader size={'default'}/>
            )}

            {status === 'success' | error?.response?.status === 401 && (
                <div className="row">
                    <div className="col-12">

                        <div className="card-box">
                            <div className="table-responsive">
                                <table className="table table-centered mb-0">
                                    <thead className="font-13 bg-light text-muted">
                                    <tr>
                                        <th className="font-weight-medium">Credit Note Number</th>
                                        <th className="font-weight-medium">Short Credit Note URL</th>
                                        <th className="font-weight-medium">Original Invoice</th>
                                        <th className="font-weight-medium">Customer Email</th>
                                        <th className="font-weight-medium">Reason</th>
                                        <th className="font-weight-medium">Total Amount</th>
                                        <th className="font-weight-medium">Type</th>
                                        <th className="font-weight-medium">Status</th>
                                        <th className="font-weight-medium">Created</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {data?.count > 0? data?.results?.map(creditNote => (
                                        <CreditNote key={creditNote.id} creditNote={creditNote}/>
                                    )): <RecordsNotFound/>}
                                    </tbody>
                                </table>
                            </div>
                            {status === 'success' && (
                                <Paginator data={data} page={page} setPage={setPage} last={data?.last_page} loading={isFetching}/>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

export default CreditNotePage;
